import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, Copy, Info, Plus, Search } from 'lucide-react';

import { ApiError, request } from '@/api/client';
import type { Club, Page } from '@/api/types';
import { PageHeader } from '@/components/AppShell';
import { DataTable, PageSizeSelect, Pagination, type Column } from '@/components/DataTable';
import { usePageSize } from '@/lib/pageSize';
import { useToast } from '@/components/Toast';
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  Field,
  IconButton,
  Modal,
  TableSkeleton,
  TextInput,
  Toggle,
} from '@/components/ui';
import { countryFlag, countryName, count, date } from '@/lib/format';
import { useDebounced, useResource, useSort } from '@/lib/hooks';

export function ClubsPage() {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [partnerOnly, setPartnerOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [pending, setPending] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const { sort, order, toggle } = useSort('name', 'asc');
  const debouncedSearch = useDebounced(search);
  const [pageSize, setPageSize] = usePageSize('clubs');

  const { data, error, loading, reload } = useResource<Page<Club>>(
    '/api/v1/admin/clubs',
    {
      search: debouncedSearch,
      partner_only: partnerOnly || undefined,
      sort,
      order,
      page,
      page_size: pageSize,
    },
    [debouncedSearch, partnerOnly, sort, order, page, pageSize],
  );

  const language = i18n.language;

  async function copyLink(club: Club) {
    try {
      await navigator.clipboard.writeText(club.invite_url);
      toast.success(t('clubs.linkCopied'));
    } catch {
      // Clipboard is refused outside a secure context; the link is on screen
      // and can be selected by hand, so this is not worth an error.
    }
  }

  async function setPartner(club: Club, isPartner: boolean) {
    setPending(club.id);
    try {
      await request(`/api/v1/admin/clubs/${club.id}/partner`, {
        method: 'PATCH',
        body: { is_partner: isPartner },
      });
      toast.success(
        t(isPartner ? 'clubs.partnerOn' : 'clubs.partnerOff', { name: club.name }),
      );
      reload();
    } catch {
      toast.failure(t('common.loadFailed'));
    } finally {
      setPending(null);
    }
  }

  const columns: Column<Club>[] = [
    {
      key: 'name',
      header: t('clubs.name'),
      sortKey: 'name',
      render: (club) => (
        <div className="min-w-0">
          <div className="truncate font-medium text-ink">{club.name}</div>
          <div className="truncate text-2xs text-ink-subtle">{club.club_code}</div>
        </div>
      ),
    },
    {
      key: 'country',
      header: t('clubs.country'),
      sortKey: 'country',
      render: (club) =>
        club.country ? (
          <span className="text-ink-muted">
            <span aria-hidden>{countryFlag(club.country)} </span>
            {countryName(club.country, language)}
          </span>
        ) : (
          <span aria-hidden className="text-ink-subtle">
            —
          </span>
        ),
    },
    {
      // Weg B: this is what gets handed to the club, so it has to be
      // copyable in one action rather than assembled by hand from the code.
      key: 'inviteUrl',
      header: t('clubs.trainerLink'),
      render: (club) => (
        <div className="flex items-center gap-2">
          <code className="truncate rounded bg-surface-sunken px-1.5 py-0.5 font-mono text-2xs text-ink-muted">
            /v/{club.club_code}
          </code>
          <IconButton
            variant="ghost"
            label={t('clubs.copyLink')}
            icon={<Copy className="h-3.5 w-3.5" />}
            onClick={() => copyLink(club)}
          />
        </div>
      ),
    },
    {
      key: 'members',
      header: t('clubs.members'),
      sortKey: undefined,
      align: 'right',
      render: (club) => count(club.member_count, language),
    },
    {
      key: 'coaches',
      header: t('clubs.coaches'),
      align: 'right',
      render: (club) => count(club.coach_count, language),
    },
    {
      key: 'partnerSince',
      header: t('clubs.partnerSince'),
      align: 'right',
      render: (club) =>
        club.partner_since ? (
          <span className="text-ink-subtle">{date(club.partner_since, language)}</span>
        ) : (
          <span aria-hidden className="text-ink-subtle">
            —
          </span>
        ),
    },
    {
      key: 'partner',
      header: t('clubs.partner'),
      sortKey: 'is_partner',
      align: 'right',
      render: (club) => (
        <div className="flex items-center justify-end gap-2">
          {club.is_partner ? <Badge tone="positive">{t('clubs.partner')}</Badge> : null}
          <Toggle
            checked={club.is_partner}
            disabled={pending === club.id}
            onChange={(next) => setPartner(club, next)}
            label={t(club.is_partner ? 'clubs.removePartner' : 'clubs.makePartner')}
          />
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('clubs.title')}
        subtitle={t('clubs.subtitle')}
        actions={
          <Button
            variant="primary"
            icon={<Plus className="h-4 w-4" />}
            onClick={() => setCreating(true)}
          >
            {t('clubs.create')}
          </Button>
        }
      />

      <div className="card">
        <div className="flex flex-wrap items-center gap-3 border-b border-line p-3">
          <div className="relative min-w-[16rem] flex-1">
            <Search
              aria-hidden
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-subtle"
            />
            <TextInput
              type="search"
              aria-label={t('common.search')}
              placeholder={t('clubs.name')}
              className="pl-9"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
            />
          </div>
          <label className="flex min-h-[2.25rem] items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-line text-brand"
              checked={partnerOnly}
              onChange={(event) => {
                setPartnerOnly(event.target.checked);
                setPage(1);
              }}
            />
            {t('clubs.partnerOnly')}
          </label>
          <PageSizeSelect
            value={pageSize}
            onChange={(next) => {
              setPageSize(next);
              setPage(1);
            }}
          />
        </div>

        {error ? (
          <ErrorState message={t('common.loadFailed')} onRetry={reload} />
        ) : loading && !data ? (
          <TableSkeleton columns={6} />
        ) : data && data.items.length === 0 ? (
          <EmptyState
            icon={<Building2 className="h-8 w-8" />}
            title={t('clubs.emptyTitle')}
            body={t('clubs.emptyBody')}
            action={
              <Button variant="primary" onClick={() => setCreating(true)}>
                {t('clubs.create')}
              </Button>
            }
          />
        ) : (
          <>
            <DataTable
              caption={t('clubs.title')}
              columns={columns}
              rows={data?.items ?? []}
              rowKey={(club) => club.id}
              sort={sort}
              order={order}
              onSort={(key) => {
                toggle(key);
                setPage(1);
              }}
            />
            <Pagination
              page={page}
              pageSize={pageSize}
              total={data?.total ?? 0}
              onPage={setPage}
            />
          </>
        )}
      </div>

      <p className="mt-3 flex items-start gap-1.5 text-xs text-ink-subtle">
        <Info aria-hidden className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        {t('clubs.partnerExplainer')}
      </p>

      {creating ? (
        <CreateClub
          onClose={() => setCreating(false)}
          onCreated={(club) => {
            setCreating(false);
            toast.success(t('clubs.created', { name: club.name }));
            reload();
          }}
        />
      ) : null}
    </>
  );
}

function CreateClub({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (club: Club) => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [name, setName] = useState('');
  const [country, setCountry] = useState('');
  const [isPartner, setIsPartner] = useState(false);
  const [busy, setBusy] = useState(false);

  const trimmed = name.trim();
  // Two letters or nothing: the column is an ISO 3166-1 alpha-2 code.
  const countryValid = country === '' || /^[A-Za-z]{2}$/.test(country);
  const valid = trimmed.length > 0 && trimmed.length <= 100 && countryValid;

  async function create() {
    setBusy(true);
    try {
      const club = await request<Club>('/api/v1/admin/clubs', {
        method: 'POST',
        body: {
          name: trimmed,
          country: country ? country.toUpperCase() : null,
          is_partner: isPartner,
        },
      });
      onCreated(club);
    } catch (error) {
      // A name already in use is the one failure worth naming: it means the
      // club is already there and its link already exists.
      const conflict = error instanceof ApiError && error.status === 409;
      toast.failure(conflict ? t('clubs.duplicateName') : t('common.loadFailed'));
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={t('clubs.create')}
      description={t('clubs.createHint')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" loading={busy} disabled={!valid} onClick={create}>
            {busy ? t('clubs.creating') : t('clubs.create')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label={t('clubs.name')}>
          {(id, describedBy) => (
            <TextInput
              id={id}
              aria-describedby={describedBy}
              maxLength={100}
              placeholder={t('clubs.namePlaceholder')}
              value={name}
              invalid={name.length > 0 && !trimmed}
              onChange={(event) => setName(event.target.value)}
            />
          )}
        </Field>
        <Field label={t('clubs.country')} hint={t('clubs.countryHint')}>
          {(id, describedBy) => (
            <TextInput
              id={id}
              aria-describedby={describedBy}
              maxLength={2}
              placeholder="DE"
              value={country}
              invalid={!countryValid}
              onChange={(event) => setCountry(event.target.value)}
            />
          )}
        </Field>
        <label className="flex items-start gap-2 text-sm text-ink">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 rounded border-line text-brand"
            checked={isPartner}
            onChange={(event) => setIsPartner(event.target.checked)}
          />
          <span>
            {t('clubs.makePartner')}
            <span className="mt-0.5 block text-xs text-ink-subtle">
              {t('clubs.partnerOnCreateHint')}
            </span>
          </span>
        </label>
      </div>
    </Modal>
  );
}
